import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { fetchChecklist, updateChecklist } from "../../../api/quality/checklist";
import ChecklistItemLinesCompactV3 from "../quality/checklists/checklistItemLinesCompactV3";

const CACHE_TTL_MS = 15 * 60 * 1000;

function escapeAttrSelector(value) {
  const raw = String(value);
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(raw);
  }
  return raw.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

/**
 * Fill checklist items inline from Tegninger (panel / pin detail).
 * Reuses ChecklistItemLinesCompactV3 — no navigation away required.
 *
 * Soft-close (pin focus / place pin): keep checklistId + in-memory cache so
 * reopen skips loadChecklist spinner and restores scroll / focused item.
 */
export const DrawingChecklistFillDialog = ({
  t,
  open,
  checklistId,
  onClose,
  onChecklistUpdated,
  onFocusDocumentPin,
  focusedDocumentId,
  /** "network" | "cache" | "cache-soft-refresh" */
  resumeMode = "network",
  onSessionSnapshot,
}) => {
  const [loading, setLoading] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const [items, setItems] = useState([]);
  const scrollRef = useRef(null);
  const scrollTopRef = useRef(0);
  const cacheRef = useRef(null);
  const pendingScrollRestoreRef = useRef(null);
  const lastResumeModeRef = useRef(resumeMode);
  const onSessionSnapshotRef = useRef(onSessionSnapshot);

  useEffect(() => {
    onSessionSnapshotRef.current = onSessionSnapshot;
  }, [onSessionSnapshot]);

  useEffect(() => {
    lastResumeModeRef.current = resumeMode;
  }, [resumeMode]);

  const emitSnapshot = useCallback(() => {
    if (!checklistId) return;
    onSessionSnapshotRef.current?.({
      checklistId,
      scrollTop: scrollTopRef.current,
      focusedDocumentId: focusedDocumentId ?? null,
      open: false,
    });
  }, [checklistId, focusedDocumentId]);

  const saveCache = useCallback(() => {
    if (!checklistId || !checklist) return;
    cacheRef.current = {
      checklistId: String(checklistId),
      checklist,
      items,
      scrollTop: scrollTopRef.current,
      savedAt: Date.now(),
    };
    emitSnapshot();
  }, [checklistId, checklist, items, emitSnapshot]);

  const clearCache = useCallback(() => {
    cacheRef.current = null;
    scrollTopRef.current = 0;
    pendingScrollRestoreRef.current = null;
  }, []);

  const applyCache = useCallback((cached) => {
    setChecklist(cached.checklist);
    setItems(cached.items || []);
    setLoading(false);
    pendingScrollRestoreRef.current =
      typeof cached.scrollTop === "number" ? cached.scrollTop : 0;
  }, []);

  const softRefreshChecklist = useCallback(async (id) => {
    if (!id) return;
    try {
      const response = await fetchChecklist(id);
      const data = response?.data || response;
      setChecklist(data);
      setItems(data?.items || []);
      cacheRef.current = {
        checklistId: String(id),
        checklist: data,
        items: data?.items || [],
        scrollTop: scrollTopRef.current,
        savedAt: Date.now(),
      };
    } catch (err) {
      // Keep cached UI; soft refresh is best-effort.
      console.warn("Soft refresh checklist fill dialog failed:", err);
    }
  }, []);

  const loadChecklist = useCallback(async () => {
    if (!checklistId) {
      setChecklist(null);
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetchChecklist(checklistId);
      const data = response?.data || response;
      setChecklist(data);
      setItems(data?.items || []);
      cacheRef.current = {
        checklistId: String(checklistId),
        checklist: data,
        items: data?.items || [],
        scrollTop: scrollTopRef.current,
        savedAt: Date.now(),
      };
    } catch (err) {
      console.error("Error loading checklist for fill dialog:", err);
      toast.error(t("drawings.viewer.checklist_fill_load_error"));
      setChecklist(null);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [checklistId, t]);

  // Hard close: drop in-memory state when checklistId clears.
  useEffect(() => {
    if (checklistId != null) return;
    setChecklist(null);
    setItems([]);
    clearCache();
  }, [checklistId, clearCache]);

  // Switching to another checklist: never flash the previous one.
  useEffect(() => {
    if (checklistId == null || !checklist) return;
    if (String(checklist.id) === String(checklistId)) return;
    setChecklist(null);
    setItems([]);
    setLoading(true);
  }, [checklistId, checklist]);

  // Soft-close: persist cache + scroll without clearing React state.
  useEffect(() => {
    if (open) return;
    if (checklistId != null && checklist) {
      saveCache();
    }
  }, [open, checklistId, checklist, saveCache]);

  // Open / reopen: prefer cache when soft-resuming.
  // Depends only on open + checklistId so item updates don't retrigger loads.
  useEffect(() => {
    if (!open || !checklistId) return;

    const mode = lastResumeModeRef.current || "network";
    const cached = cacheRef.current;
    const cacheHit =
      cached &&
      String(cached.checklistId) === String(checklistId) &&
      Date.now() - (cached.savedAt || 0) < CACHE_TTL_MS;

    const inMemoryHit =
      checklist && String(checklist.id) === String(checklistId);

    if (mode !== "network" && (inMemoryHit || cacheHit)) {
      if (!inMemoryHit && cacheHit) {
        applyCache(cached);
      } else {
        setLoading(false);
        pendingScrollRestoreRef.current = scrollTopRef.current;
      }
      if (mode === "cache-soft-refresh") {
        softRefreshChecklist(checklistId);
      }
      return;
    }

    if (inMemoryHit) {
      // Same id still in memory — avoid spinner (pin-focus / soft reopen).
      setLoading(false);
      pendingScrollRestoreRef.current = scrollTopRef.current;
      return;
    }

    loadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only reopen/id
  }, [open, checklistId]);

  // Restore scroll after paint when reopening from cache.
  useLayoutEffect(() => {
    if (!open || loading) return;
    const top = pendingScrollRestoreRef.current;
    if (top == null || !scrollRef.current) return;
    scrollRef.current.scrollTop = top;
    scrollTopRef.current = top;
    pendingScrollRestoreRef.current = null;
  }, [open, loading, checklist?.id, items]);

  // Scroll focused document / item into view after restore (pin-focus return).
  useEffect(() => {
    if (!open || loading || !focusedDocumentId || !scrollRef.current) return;
    const root = scrollRef.current;
    const escaped = escapeAttrSelector(focusedDocumentId);
    const docEl = root.querySelector(`[data-drawing-doc-id="${escaped}"]`);
    if (docEl) {
      docEl.scrollIntoView({ block: "nearest", behavior: "auto" });
      scrollTopRef.current = root.scrollTop;
      return;
    }
    // Fallback: item that owns the document (if marked).
    const itemEl = root.querySelector(
      `[data-drawing-focused-doc="${escaped}"]`
    );
    if (itemEl) {
      itemEl.scrollIntoView({ block: "nearest", behavior: "auto" });
      scrollTopRef.current = root.scrollTop;
    }
  }, [open, loading, focusedDocumentId, items]);

  const handleItemsUpdate = useCallback(
    (updatedItems) => {
      setItems(updatedItems);
      setChecklist((prev) => {
        const next = prev ? { ...prev, items: updatedItems } : prev;
        if (checklistId) {
          cacheRef.current = {
            checklistId: String(checklistId),
            checklist: next || cacheRef.current?.checklist,
            items: updatedItems,
            scrollTop: scrollTopRef.current,
            savedAt: Date.now(),
          };
        }
        return next;
      });
      onChecklistUpdated?.(checklistId, updatedItems);
    },
    [checklistId, checklist, onChecklistUpdated]
  );

  const handleHardClose = useCallback(() => {
    clearCache();
    onClose?.();
  }, [clearCache, onClose]);

  if (!checklistId) return null;

  const title =
    checklist?.displayTitle ||
    checklist?.title ||
    (checklist?.number != null ? `#${checklist.number}` : t("drawings.module.checklist"));

  return (
    <PMDialog
      open={open}
      onClose={handleHardClose}
      size="4xl"
      zIndex={140}
      panelClassName="!p-0 sm:!p-0"
    >
      <div className="flex h-full max-h-[var(--vv-height,100dvh)] flex-col overflow-hidden sm:max-h-[min(94dvh,860px)]">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#e5e8ec] px-3 py-2 sm:px-4 sm:py-3.5">
          <div className="flex min-w-0 items-center gap-2 sm:gap-2.5">
            <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-[#2b6ef0] text-white">
              <ClipboardDocumentCheckIcon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-[15px] font-semibold text-[#14171c]">
                {title}
              </h2>
              <p className="text-[11px] text-[#59616d]">
                {t("drawings.viewer.checklist_fill_subtitle")}
                {checklist?.house || checklist?.apartment
                  ? ` · ${[checklist.house, checklist.apartment].filter(Boolean).join(" / ")}`
                  : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {checklistId && (
              <Link
                to={`/dashboard/hse-ks/checklists/${checklistId}`}
                className="grid h-9 w-9 place-items-center rounded-md text-[#2b6ef0] hover:bg-[#eaf1fe] sm:h-8 sm:w-8"
                title={t("drawings.viewer.open_checklist")}
              >
                <ArrowTopRightOnSquareIcon className="h-5 w-5" />
              </Link>
            )}
            <button
              type="button"
              onClick={handleHardClose}
              className="grid h-9 w-9 place-items-center rounded-md text-[#59616d] hover:bg-[#eef0f3] sm:h-8 sm:w-8"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2 sm:px-3 sm:py-3"
          onScroll={(event) => {
            scrollTopRef.current = event.currentTarget.scrollTop;
          }}
        >
          {loading ? (
            <ResourceLoading size="sm" />
          ) : !checklist ? (
            <p className="p-4 text-sm text-[#59616d]">
              {t("drawings.viewer.checklist_fill_load_error")}
            </p>
          ) : (
            <ChecklistItemLinesCompactV3
              checklist={checklist}
              items={items}
              checklistId={checklist.id}
              project={checklist.project}
              linkedDrawing={checklist.linked_drawing}
              editable={checklist.is_items_editable !== false}
              disabled={
                checklist.is_items_editable !== undefined &&
                !checklist.is_items_editable
              }
              variant="drawings"
              onFocusDocumentPin={onFocusDocumentPin}
              focusedDocumentId={focusedDocumentId}
              updateChecklistItems={async (id, payload) => {
                return await updateChecklist({
                  id,
                  items: payload?.checklist?.items || [],
                });
              }}
              onItemsUpdate={handleItemsUpdate}
            />
          )}
        </div>
      </div>
    </PMDialog>
  );
};
